import moment from 'moment';
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Components from '../../components';
import Hooks from '../../hooks';
import { fetchAttractionsDetails } from '../../modules/location/details/operations';
import Calendar from './Calendar';
import { useSearchParams } from 'react-router-dom';
import { TicketPrefills } from '../../config/globalVariables';
import { getAttractionsDefaults } from '../../modules/location/settings/selectors';
import snackbar from '../../utils/snackbar';
import OutOfService from '../../components/OutOfService/OutOfService';
import visibleOnRelease from '../../config/visibleOnRelease';
import {
  getProductsTypes,
  getProductsEntries,
  getAttractionsClient,
} from '../../modules/attractions/selectors';
import {
  fetchProduct,
  fetchProductTypes,
  fetchAttractionsClient,
} from '../../modules/attractions/operations';
import { resetPickedAttractionsPasses } from '../../modules/attractions/slice';
import getClientsStyle from '../../utils/getClientsStyle';
import { IAttractionsClient } from '../../modules/common/models/productModels';
import '../../components/AttractionsTemplate/AttractionsTemplate.scss';
// import { FaSearchPlus } from 'react-icons/fa';
// import largeFakeMap from '../../assets/images/pogladowa_mapa.jpg';
// import smallFakeMap from '../../assets/images/pogladowa_mapa.jpg';
// import logoBramaPng from '../../assets/logo/logo_gorce.png';
// import logoGoracyPotok from '../../assets/logo/logo_potok.png';
// import logoStartvelo from '../../assets/logo/logo_startvelo.png';
// import logoRabkoland from '../../assets/logo/logo_rabkoland.png';
import Tooltip from '@mui/material/Tooltip';

const Attractions = () => {
  const { t } = useTranslation(['pages', 'components']);
  const BUILD_TYPE = process.env.REACT_APP_BUILD_TYPE?.toUpperCase();
  const styleForClient = getClientsStyle();
  // const [isClicked, setIsClicked] = useState(false);
  // const [isHovered, setIsHovered] = useState(false);
  const [date, setDate] = useState<string | null>(null);
  const idClients = Hooks.useAppSelector((s) =>
    getAttractionsClient(s.attractions)
  );
  const [selectedAttractionIds, setSelectedAttractionIds] = useState<number[]>(
    []
  );

  const typeId = Hooks.useAppSelector((s) => {
    const productsTypes = getProductsTypes(s?.attractions);
    return productsTypes?.length > 0 ? productsTypes[0]?.id : '';
  });

  const selectAllClients = () => {
    setSelectedAttractionIds(
      idClients?.map((attraction) => attraction.id) || []
    );
  };

  // Mapa zablokowanych atrakcji
  const LOCK_MAP: Record<string, string[]> = {
    RABKOLAND: ['Rabkoland'],
    BRAMA: ['Brama w Gorce'],
    STARTVELO: ['Startvelo', 'Start Velo - Rowery Elektryczne'],
    POTOK: ['Termy Gorący Potok'],
    FLISACY: ['Flisacy'],
    POLANA: ['Długa Polana'],
  };

  const lockedNames = useMemo(() => {
    const fromEnv = process.env.REACT_APP_LOCKED_CLIENTS?.split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (fromEnv?.length) return fromEnv;
    return LOCK_MAP[BUILD_TYPE ?? ''] ?? [];
  }, [BUILD_TYPE]);

  const lockedAttractionIds = useMemo(() => {
    if (!idClients) return [];
    const wanted = new Set(lockedNames.map((n) => n.toLowerCase()));
    return idClients
      .filter((c) => wanted.has(c.name.toLowerCase()))
      .map((c) => c.id);
  }, [idClients, lockedNames]);

  const isLocked = (id: number) => lockedAttractionIds.includes(id);

  function mergeLockedAttractionIds(
    prev: number[],
    lockedAttractionIds: number[]
  ) {
    const need = lockedAttractionIds.filter((id) => !prev.includes(id));
    return need.length
      ? Array.from(new Set([...prev, ...lockedAttractionIds]))
      : prev;
  }

  useEffect(() => {
    if (!lockedAttractionIds.length) return;
    setSelectedAttractionIds((prev) =>
      mergeLockedAttractionIds(prev, lockedAttractionIds)
    );
  }, [lockedAttractionIds]);

  useEffect(() => {
    if (idClients && idClients.length > 0) {
      selectAllClients();
    }
  }, [idClients]);

  const dispatch = Hooks.useAppDispatch();
  const numberOfPersons = Hooks.useAppSelector((s) =>
    getProductsTypes(s.attractions)
  );

  const attractionsPasses = Hooks.useAppSelector((s) =>
    getProductsEntries(s.attractions)
  );

  const [params] = useSearchParams();
  const prefill = params.get('prefill') ?? TicketPrefills.Today;

  const defaults = Hooks.useAppSelector((s) =>
    getAttractionsDefaults(s.settings)
  );

  const attractionsPassesContent = useMemo(
    () =>
      attractionsPasses?.map((attraction) => ({
        ...attraction,
        isHighlighted: true,
      })),
    [attractionsPasses]
  );

  const typeDropdown = numberOfPersons?.map((type) => ({
    label: type.name,
    value: type.id,
  }));

  const resetDate = () => {
    const now = moment();

    if (
      now.isBetween(
        moment(defaults?.maximumBuyingTimeForToday, 'hh:mm:ss'),
        moment('23:59:59', 'hh:mm:ss')
      )
    ) {
      setDate(now.add(1, 'days').format());
      snackbar({
        type: 'warning',
        msg: t('pages:activities.tickets.nextDayWarning'),
      });
    } else {
      setDate(moment().toISOString());
    }
  };

  // const tomorrowDate = () => {
  //   const now = moment();
  //   setDate(now.add(1, 'days').format());
  // };

  const onDateChange = (date: string) => {
    setDate(date);
  };

  useEffect(() => {
    if (date) {
      dispatch(fetchProductTypes());
      dispatch(fetchAttractionsClient());
      dispatch(fetchAttractionsDetails());
    }
  }, [date, dispatch]);

  useEffect(() => {
    if (typeId && date && selectedAttractionIds?.length > 0) {
      dispatch(fetchProduct({ typeId, date, selectedAttractionIds }));
    }

    return () => {
      dispatch(resetPickedAttractionsPasses());
    };
  }, [selectedAttractionIds, dispatch, typeId, date]);

  useEffect(() => {
    const now = moment(defaults?.localDateTime);
    if (
      prefill === TicketPrefills.Today &&
      !!defaults?.maximumBuyingTimeForToday
    ) {
      if (
        now.isBefore(moment(defaults?.maximumBuyingTimeForToday, 'HH:mm:ss'))
      ) {
        setDate(now.format());
      } else {
        setDate(now.add(1, 'days').format());
        snackbar({
          type: 'warning',
          msg: t('pages:activities.tickets.nextDayWarning'),
        });
      }
    }
  }, [
    defaults?.localDateTime,
    defaults?.maximumBuyingTimeForToday,
    prefill,
    t,
  ]);

  const toggleClient = (clientId: number) => {
    if (isLocked(clientId)) return; // nie odznaczysz zablokowanych

    const idx = selectedAttractionIds.indexOf(clientId);
    if (idx === -1) {
      setSelectedAttractionIds([...selectedAttractionIds, clientId]);
    } else {
      const next = [...selectedAttractionIds];
      next.splice(idx, 1);
      setSelectedAttractionIds(next);
    }
  };

  const toggleAllClient = () => {
    setSelectedAttractionIds((prev) => {
      const allIds = idClients?.map((a) => a.id) ?? [];
      const allSelected = prev.length === allIds.length;
      if (allSelected) {
        // odznacz wszystko → zostaw tylko zablokowane
        return lockedAttractionIds;
      }
      // zaznacz wszystko → wszystkie (w tym zablokowane)
      return allIds.length ? allIds : lockedAttractionIds;
    });
  };

  return (
    <>
      <div className="berg-pages-activities__attractions">
        <Components.FilterBar
          // defaultDropdownOptionValue={
          //   prefill === TicketPrefills.Today
          //     ? defaults?.defaultTypeId
          //     : undefined
          // }
          defaultDropdownOptionValue={defaults?.defaultTypeId}
          typeDropdown={date ? typeDropdown : undefined}
          label={t('pages:activities.ticket_type')}
          Calendar={<Calendar date={date} onDateChange={onDateChange} />}
          // onTypeChange={handleTypeChange}
          date={date}
          resetDate={resetDate}
          // tomorrowDate={tomorrowDate}
        />
        <hr />
        {date && (
          <div className="flex flex-col gap-4 sm:gap-8 text-start berg-components-attractions-template__filter-button">
            <div
              className="flex flex-row gap-2 flex-wrap sm:gap-6 items-center justify-center sm:justify-start"
              data-client-style={styleForClient}
            >
              {idClients?.map((attraction: IAttractionsClient) => (
                <Tooltip
                  key={attraction.id}
                  title={attraction.description || 'Brak opisu'}
                  arrow
                  placement="top"
                  disableFocusListener
                  disableTouchListener
                  enterDelay={80}
                  leaveDelay={40}
                  slotProps={{
                    tooltip: {
                      sx: {
                        bgcolor: '#616161eb',
                        color: '#fff',
                        fontSize: '1rem',
                        lineHeight: 1.4,
                        px: '10px',
                        py: '8px',
                        borderRadius: '10px',
                        boxShadow: '0 8px 24px rgba(0,0,0,.15)',
                        maxWidth: 320,
                        whiteSpace: 'pre-line',
                      },
                    },
                    arrow: { sx: { color: '#616161eb' } },
                    popper: {
                      popperOptions: { strategy: 'fixed' },
                      modifiers: [
                        {
                          name: 'flip',
                          options: {
                            fallbackPlacements: [
                              'bottom',
                              'top-start',
                              'top-end',
                            ],
                          },
                        },
                        {
                          name: 'preventOverflow',
                          options: {
                            boundary: 'viewport',
                            altAxis: true,
                            padding: 8,
                          },
                        },
                        { name: 'computeStyles', options: { adaptive: false } },
                      ],
                    },
                  }}
                >
                  <span className="inline-flex items-center pointer-events-auto">
                    <a
                      onClick={() => toggleClient(attraction?.id)}
                      className={
                        (selectedAttractionIds.includes(attraction.id)
                          ? 'button-active'
                          : 'button-inactive') +
                        (isLocked(attraction.id)
                          ? '  cursor-not-allowed drop-shadow-[2px_4px_6px_rgba(0,0,0,0.80)]'
                          : '')
                      }
                      aria-disabled={isLocked(attraction.id) ? true : undefined}
                      data-client-style={styleForClient}
                    >
                      {/* Renderowanie ikon */}
                      {/* {attraction.name === 'Termy Gorący Potok' ? (
                        <img
                          src={logoGoracyPotok}
                          alt="Gorący Potok"
                          className="w-[1.5rem] sm:w-[3rem]"
                        />
                      ) : attraction.name === 'Brama w Gorce' ? (
                        <img
                          src={logoBramaPng}
                          alt="Brama w Gorce"
                          className="w-[1.2rem] sm:w-[2.5rem]"
                        />
                      ) : attraction.name === 'StartVelo' ||
                        attraction.name ===
                          'Start Velo - Rowery Elektryczne' ? (
                        <img
                          src={logoStartvelo}
                          alt="StartVelo"
                          className="w-[1.2rem] sm:w-[2.5rem] overflow-hidden rounded-full"
                        />
                      ) : attraction.name === 'Rabkoland' ? (
                        <img
                          src={logoRabkoland}
                          alt="Rabkoland"
                          className="w-[1.2rem] sm:w-[2.5rem] overflow-hidden rounded-full"
                        />
                      ) : (
                        ''
                      )} */}

                      {attraction.name}
                    </a>
                  </span>
                </Tooltip>
              ))}

              <a
                onClick={() => toggleAllClient()}
                className={`button-all ${
                  selectedAttractionIds?.length === 4
                    ? 'button-active'
                    : 'button-inactive'
                }`}
                data-client-style={styleForClient}
              >
                {t('components:attractions.filter_all')}
              </a>
            </div>
            <p className="text-center sm:text-start">
              {t('components:attractions.filter')}
            </p>
          </div>
        )}
      </div>

      {visibleOnRelease.outOfService && <OutOfService />}
      {attractionsPassesContent && attractionsPassesContent.length === 0 && (
        <p className="text-[24px] font-semibold my-12">
          {t('components:attractions.no_passes')}
        </p>
      )}
      <Components.AttractionsTemplate attractions={attractionsPassesContent} />
      {/* <div
        className="relative"
        onClick={() => setIsClicked(!isClicked)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <img
          src={isClicked ? largeFakeMap : smallFakeMap}
          alt="map"
          className="w-[200px] h-auto rounded-2xl overflow-hidden"
        />
        {isHovered && (
          <div className="absolute top-0 left-0 w-full h-full bg-[#00000080] flex justify-center items-center rounded-2xl cursor-pointer">
            <FaSearchPlus className="text-white mr-[5px] cursor-pointer" />
            <p className="text-white text-base cursor-pointer">
              {t('components:attractions.zoom')}
            </p>
          </div>
        )}
      </div>
            {isClicked && (
        <div className="berg-information-image-map-background ">
          <div className="berg-information-image-map-image">
            <button
              className="absolute top-5 right-5"
              onClick={() => setIsClicked(!isClicked)}
            >
              <Icons.ClosePopup />
            </button>
            <img className="w-full" src={largeFakeMap} alt="Duże zdjęcie" />
          </div>
        </div>
      )} */}
      <iframe
        src="https://www.google.com/maps/d/u/2/embed?mid=1zLdbJQZ8t__VzrIRuuJfT42oAD836ks&ehbc=2E312F&noprof=1"
        height="480"
        style={{ width: '100%', border: 'none' }}
        className="pl-4 pr-4 sm:pl-0 pr-0"
      ></iframe>
    </>
  );
};

export default Attractions;
